import {Card, DataStatus, Table, TableRow, Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  useGetMakeOrderStatusSignalMappingQuery,
  type MakeOrderStatusSignalMapping,
} from '@dms/api/new-vehicle';

import {Cell, column, t} from '../../catalogueUi';

const targetLabel = (mapping: MakeOrderStatusSignalMapping) =>
  match(mapping)
    .with({targetKind: 'informational'}, () =>
      t('entity.newVehicle.enums.orderSignal.informational', 'Informational only')
    )
    .with({targetKind: 'signal', mappedSignal: 'delivered'}, () =>
      t('entity.newVehicle.enums.orderSignal.delivered', 'Delivered')
    )
    .with({targetKind: 'signal', mappedSignal: 'cancelled'}, () =>
      t('entity.newVehicle.enums.orderSignal.cancelled', 'Cancelled')
    )
    .with({targetKind: 'signal', mappedSignal: 'retail_sold'}, () =>
      t('entity.newVehicle.enums.orderSignal.retailSold', 'Retail sold')
    )
    .with({targetKind: 'signal', mappedSignal: null}, () =>
      t('entity.newVehicle.enums.statusLogTarget.signal')
    )
    .exhaustive();

type OrderStatusSignalMappingCardProps = {
  makeId: string;
};

/** Importer statuses that raise an order signal or are only recorded, next to the stage mapping. */
export function OrderStatusSignalMappingCard(props: OrderStatusSignalMappingCardProps) {
  const query = useGetMakeOrderStatusSignalMappingQuery({makeId: props.makeId});
  const mappings = query.data?.mappings ?? [];

  return (
    <Card
      data-testid="orderStatusSignalMapping"
      title={t(
        'entity.newVehicle.catalogue.importerOrderStatusSignals',
        'Importer order-status signals'
      )}
    >
      <DataStatus
        data-testid="orderStatusSignalMapping"
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={!mappings.length}
      >
        <Table
          columns={[
            column(t('entity.newVehicle.catalogue.importerStatus', 'Importer status')),
            column(t('entity.newVehicle.catalogue.usage', 'Usage'), {
              width: '14%',
              align: 'right',
            }),
            column(t('entity.newVehicle.catalogue.signalTarget', 'Signal'), {width: '30%'}),
          ]}
        >
          {mappings.map((mapping) => (
            <TableRow key={mapping.rawStatus}>
              <Cell>
                <Tooltip
                  label={t('entity.newVehicle.catalogue.rawImporterStatus', 'Raw importer status')}
                >
                  <Text size="small">{mapping.rawStatus}</Text>
                </Tooltip>
              </Cell>
              <Cell align="right">{mapping.usageCount}</Cell>
              <Cell>
                <Text size="small">{targetLabel(mapping)}</Text>
              </Cell>
            </TableRow>
          ))}
        </Table>
      </DataStatus>
    </Card>
  );
}
